import {
  ButtonSelectChoice,
  RawScenario, RawScenarioPageInfoAddress, RawScenarioPageInfoAgreement,
  RawScenarioPageInfoButtonSelect,
  RawScenarioPageInfoEmail, RawScenarioPageInfoGenderAndBirthDate,
  RawScenarioPageInfoInputSubQuery, RawScenarioPageInfoMainComplaint,
  RawScenarioPageInfoNameKana,
  RawScenarioPageInfoNumText,
  RawScenarioPageInfoSelectSubQuery, RawScenarioPageInfoTel,
} from '../types/rawScenario';
import { FormValues } from "../stores/formState";
import { toOrderedComponentId } from "./idMapping";
import { ScenarioItem } from '../stores/scenarioState';

export function applyFormToRawScenario(scenario: RawScenario, form: FormValues) {

  for (const pageInfo of scenario.scenarioPageInfo) {
    switch (pageInfo.template) {
      case 'ボタン選択':
      case 'ボタン選択(画像有)':
      case 'ボタン選択(画像説明付き)':
      case 'ボタン選択(動画説明付き)':
      case '主訴':
        applyFormToSelect(pageInfo, form);
        break;
      case '数字、文字入力':
        applyFormToTextNum(pageInfo, form);
        break;
      case 'かな氏名':
        applyFormToNameKana(pageInfo, form);
        break;
      case 'メール':
        applyFormToEmail(pageInfo, form);
        break;
      case '電話番号':
        applyFormToTel(pageInfo, form);
        break;
      case '住所入力':
        applyFormAddress(pageInfo, form);
        break;
      case 'ページ内副問い合わせ(ボタン選択)':
        applyFormToSelectSubquery(pageInfo, form);
        break;
      case 'ページ内副問い合わせ(入力)':
        applyFormToInputSubquery(pageInfo, form);
        break;
      case '生年月日':
        applyFormToGenderAndBirthDate(pageInfo, form);
        break;
      case '同意ページ':
        applyFormToAgreement(pageInfo, form);
    }
  }

  if (scenario.personalInfo == null) {
    const [fname1, fname2] = String(form['--personal-info:fname'] ?? '').match(/^([^\s　]*)[\s　]*(.*)$/)?.slice(1) ?? ['', ''];
    scenario.personalInfo = {
      fname1,
      fname2,
      reserveDate: String(form['--personal-info:reserve-date'] ?? ''),
    }
  }
}

function applyFormToSelect(pageInfo: RawScenarioPageInfoButtonSelect | RawScenarioPageInfoMainComplaint, form: FormValues) {
  applyFormToSelectQuestion(pageInfo.detail, form, pageInfo.page_id);
}

function applyFormToTextNum(pageInfo: RawScenarioPageInfoNumText, form: FormValues) {
  for (const question of pageInfo.detail.input_question) {
    question.value = form[question.item_id] != null ? String(form[question.item_id]) : '';
  }
}

function applyFormToNameKana(pageInfo: RawScenarioPageInfoNameKana, form: FormValues) {
  const key = '--personal-info:fname' in form ? '--personal-info:fname' : pageInfo.page_id;
  pageInfo.detail.patient_name = form[key] != null ? String(form[key]) : '';
}
function applyFormToEmail(pageInfo: RawScenarioPageInfoEmail, form: FormValues) {
  const value = form[pageInfo.page_id];
  pageInfo.detail.mail_address = value != null ? String(value) : '';
}
function applyFormToTel(pageInfo: RawScenarioPageInfoTel, form: FormValues) {
  pageInfo.detail.tel_question.forEach((tel, i) => {
    const value = form[toOrderedComponentId(pageInfo.page_id, i)];
    tel.tel_num = value != null ? String(value) : '';
  });
}
function applyFormAddress(pageInfo: RawScenarioPageInfoAddress, form: FormValues) {
  const postalCode = getValue(form, toOrderedComponentId(pageInfo.page_id, 0));
  pageInfo.detail.post_code_question.post_code = postalCode?.replace(/-/g, '');
  pageInfo.detail.address_question1.before_num_address = getValue(form, toOrderedComponentId(pageInfo.page_id, 1));
  pageInfo.detail.address_question2.after_num_address = getValue(form, toOrderedComponentId(pageInfo.page_id, 2));
}
function applyFormToSelectSubquery(pageInfo: RawScenarioPageInfoSelectSubQuery, form: FormValues) {
  pageInfo.detail.btn_select_questions.forEach((question, i) => {
    applyFormToSelectQuestion(question, form, toOrderedComponentId(pageInfo.page_id, i));
  });
}
function applyFormToInputSubquery(pageInfo: RawScenarioPageInfoInputSubQuery, form: FormValues) {
  applyFormToSelectQuestion(pageInfo.detail.btn_select_question, form, toOrderedComponentId(pageInfo.page_id, 0));
  pageInfo.detail.input_question.questions.forEach(question => {
    const value = form[question.item_id];
    question.value = value != null ? String(value) : '';
  })
}

function applyFormToSelectQuestion(question: {choices: ButtonSelectChoice[]}, form: FormValues, id: string) {
  const value = form[id];
  for (const choice of question.choices) {
    if (value instanceof Array) {
      choice.selected = value.includes(choice.disp_name) ? '1' : '0';
    } else {
      choice.selected = choice.disp_name === value ? '1' : '0';
    }
  }
}

function applyFormToGenderAndBirthDate(pageInfo: RawScenarioPageInfoGenderAndBirthDate, form: FormValues) {
  applyFormToSelectQuestion(pageInfo.detail.gender_question, form, toOrderedComponentId(pageInfo.page_id, 0));

  const epoch = Date.parse(String(form[toOrderedComponentId(pageInfo.page_id, 1)]));
  if (Number.isNaN(epoch)) return;
  const date = new Date(epoch);

  const fullYear = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const ymd = fullYear * 10000 + month * 100 + day;
  let era = '';
  let year = fullYear;
  if (ymd < 18680101) {
  } else if (ymd < 19120730) {
    era = '明治';
    year -= 1867;
  } else if (ymd < 19261225) {
    era = '大正';
    year -= 1911;
  } else if (ymd < 19890108) {
    era = '昭和';
    year -= 1925;
  } else if (ymd < 20190501) {
    era = '平成';
    year -= 1988;
  } else {
    era = '令和';
    year -= 2018;
  }

  pageInfo.detail.era_question.choices.forEach(c => {
    if (c.disp_name === era) {
      c.selected = '1';
    }
  });
  const zeropad = (n: number) => (n < 10 ? '0' : '') + String(n) as `${number}`;
  pageInfo.detail.birth_question.birth_year = zeropad(year);
  pageInfo.detail.birth_question.birth_month = zeropad(month);
  pageInfo.detail.birth_question.birth_day = zeropad(day);
}

function applyFormToAgreement(pageInfo: RawScenarioPageInfoAgreement, form: FormValues) {
  applyFormToSelectQuestion({choices: pageInfo.detail.choices}, form, toOrderedComponentId(pageInfo.page_id, 2));
}

function getValue(form: FormValues, id: string) {
  const value = form[id];
  return value != null ? String(value) : '';
}

export function applyAnswerStatusToRawScenario(scenario: RawScenario, usingScenarioItems: ScenarioItem[]) {
  scenario.scenarioPageInfo.forEach(item =>
    item.answer_status = usingScenarioItems.some(i => i.id === item.page_id) ? '1' : '0');
  // かな氏名は事前に入力させる項目なので、入力されていれば回答済みとする
  scenario.scenarioPageInfo.forEach(item => {
    if (item.template !== 'かな氏名') return;
    if (!item.detail.patient_name) return;
    item.answer_status = '1';
  });
}
